import type { PublicFormConfig, RegisterResponse } from '@ffs/shared';
import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { ApiError, getForm, getRegistration } from '../lib/api';
import type { MountOptions } from '../lib/options';
import { Confirmation } from './Confirmation';
import { RegisterForm } from './RegisterForm';

const DEFAULT_ACCENT = '#1d5fe0';

/** Dark text on a light button, white on a dark one (relative luminance of "#rrggbb"). */
export function buttonTextColor(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const ch = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const lum = 0.2126 * ch(n >> 16) + 0.7152 * ch((n >> 8) & 255) + 0.0722 * ch(n & 255);
  return lum > 0.45 ? '#0f172a' : '#ffffff';
}

/**
 * Shadow-root wrapper: resets inherited styles and resolves the accent (host var → branding).
 * `buttonColor` colours the submit button only; everything else keeps the accent.
 */
export function Shell({
  accent,
  buttonColor,
  hosted,
  children,
}: {
  accent: string | null;
  buttonColor?: string | null;
  hosted: boolean;
  children: ComponentChildren;
}) {
  const style = useMemo(
    () => ({
      '--a': `var(--ffs-accent, ${accent ?? DEFAULT_ACCENT})`,
      ...(buttonColor ? { '--btn': buttonColor, '--btn-fg': buttonTextColor(buttonColor) } : {}),
    }),
    [accent, buttonColor],
  );
  return (
    <div class="ffs" style={style}>
      <div class={`card${hosted ? ' flat' : ''}`}>{children}</div>
    </div>
  );
}

export function Notice({ children, busy }: { children: ComponentChildren; busy?: boolean }) {
  return (
    <p class="empty" role="status" aria-live="polite" aria-busy={busy}>
      {children}
    </p>
  );
}

export function registeredTitle(res: Pick<RegisterResponse, 'already_registered' | 'rescheduled'>) {
  if (res.rescheduled) return "You're rescheduled";
  if (res.already_registered) return "You're already registered";
  return "You're in";
}

export function RegisteredConfirmation({ res }: { res: RegisterResponse }) {
  return (
    <Confirmation
      title={registeredTitle(res)}
      when={res.session_start_label}
      subtitle={
        res.already_registered && !res.rescheduled
          ? 'Your seat is saved. We re-sent the details to your email.'
          : 'A confirmation with your join link is on its way to your inbox.'
      }
      joinUrl={res.join_url}
      calendar={res.calendar}
    />
  );
}

/** `data-ffs-registered="<token>"`: a confirmation for a registration made elsewhere (one-click). */
function AlreadyRegistered({ o, token }: { o: MountOptions; token: string }) {
  const [res, setRes] = useState<RegisterResponse | null>(null);
  useEffect(() => {
    getRegistration(o.base, token)
      .then(setRes)
      .catch(() => setRes(null));
  }, [o.base, token]);
  const t = encodeURIComponent(token);
  // Until the details arrive (or if they cannot be loaded) the token alone is enough for the
  // join link and the calendar file.
  return (
    <Confirmation
      title="You're in"
      when={res?.session_start_label ?? null}
      subtitle="A confirmation with your join link is on its way to your inbox."
      joinUrl={res?.join_url ?? `${o.base}/j/${t}`}
      calendar={res?.calendar ?? { ics: `${o.base}/cal/${t}.ics` }}
    />
  );
}

export function App({ o }: { o: MountOptions }) {
  const [cfg, setCfg] = useState<PublicFormConfig | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [result, setResult] = useState<RegisterResponse | null>(null);

  useEffect(() => {
    if (!o.publicId) return;
    let alive = true;
    getForm(o.base, o.publicId)
      .then((c) => alive && setCfg(c))
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && (e.code === 'webinar_inactive' || e.code === 'not_found'))
          setFatal(
            e.code === 'not_found'
              ? 'This registration form could not be found.'
              : 'This webinar is not accepting registrations right now.',
          );
        else setFatal('We could not load the registration form. Please refresh to try again.');
      });
    return () => {
      alive = false;
    };
  }, [o.base, o.publicId]);

  const accent = cfg?.webinar.branding.accent ?? null;
  let body: ComponentChildren;
  if (o.registeredToken) body = <AlreadyRegistered o={o} token={o.registeredToken} />;
  else if (fatal) body = <Notice>{fatal}</Notice>;
  else if (result) body = <RegisteredConfirmation res={result} />;
  else if (!cfg)
    body = (
      <div class="slots" role="status" aria-busy="true" aria-label="Loading registration form">
        <div class="skel short" />
        <div class="skel" />
        <div class="skel" />
        <div class="skel" />
      </div>
    );
  else body = <RegisterForm cfg={cfg} o={o} onDone={setResult} onInactive={setFatal} />;

  return (
    // The form's button color applies to the form only; confirmations use the webinar's accent.
    <Shell
      accent={accent}
      buttonColor={result || o.registeredToken ? null : (cfg?.button_color ?? null)}
      hosted={o.hosted || o.flat}
    >
      {body}
    </Shell>
  );
}
